import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "Amazon商品画像サイズ 2026年最新 1000×1000以上が必須な理由と最適設定",
	description:
		"Amazon出品で1000×1000px以上が必須な理由（ズーム機能）と、推奨1600×1600px・白背景の設定方法を2026年版で詳しく解説。",
	publishDate: "2026-03-16",
	slug: "amazon-product-image-size-2026",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function AmazonProductImageSize2026() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<p className="text-gray-700 mb-6">
						Amazonに商品を出品する際、「画像サイズは最低どのくらい必要？」「1000×1000でいいの？」と迷ったことはありませんか？
						実はAmazonの画像サイズ要件には明確な理由があり、設定を誤ると販売機会を大きく損ないます。2026年最新の要件を詳しく解説します。
					</p>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							Amazon商品画像の基本要件（2026年）
						</h2>
						<div className="overflow-x-auto mb-4">
							<table className="w-full text-sm border-collapse border border-gray-200">
								<thead>
									<tr className="bg-gray-50">
										<th className="border border-gray-200 px-4 py-2 text-left">
											項目
										</th>
										<th className="border border-gray-200 px-4 py-2 text-left">
											要件
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											最小サイズ（登録可能）
										</td>
										<td className="border border-gray-200 px-4 py-2">
											長辺 500px 以上
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											ズーム機能有効の最小サイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											<strong>1000×1000px 以上</strong>
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											推奨サイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											<strong>1600×1600px 以上</strong>
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											最大サイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											長辺 10,000px 以内
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											ファイル形式
										</td>
										<td className="border border-gray-200 px-4 py-2">
											JPEG（推奨）、PNG、GIF（静止画のみ）
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											メイン画像の背景色
										</td>
										<td className="border border-gray-200 px-4 py-2">
											<strong>純粋な白（RGB 255,255,255）必須</strong>
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											最大登録枚数
										</td>
										<td className="border border-gray-200 px-4 py-2">9枚</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							なぜ1000×1000px以上が「必須」なのか
						</h2>
						<p className="text-gray-700 mb-4">
							Amazonには<strong>ズーム機能</strong>
							があり、購入者が商品画像にカーソルを合わせると拡大表示されます。
							このズーム機能は<strong>長辺が1000px以上の画像でのみ有効</strong>
							になります。
						</p>
						<p className="text-gray-700 mb-4">
							ズームで商品の細部（素材感、縫い目、刻印など）を確認できると、購入者の不安が解消され、
							<strong>購入率（CVR）が大幅に向上</strong>
							することがAmazonのデータから示されています。
							逆に1000px未満だとズームが無効になり、競合商品に対して不利な立場になります。
						</p>
						<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
							<p className="text-sm text-yellow-800">
								<strong>⚠️ 注意</strong>
								：500px以上あればAmazonへの登録自体は可能ですが、ズーム機能が使えないため販売機会の損失につながります。最低でも1000×1000px、推奨は1600×1600px以上で設定しましょう。
							</p>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							メイン画像の白背景ルール
						</h2>
						<p className="text-gray-700 mb-4">
							Amazonでは
							<strong>
								1枚目のメイン画像に純粋な白背景（RGB: 255,255,255）
							</strong>
							が必須です。
							グレーがかった白や生成り色はNGです。このルールに違反すると：
						</p>
						<ul className="list-disc list-inside space-y-2 text-gray-700 mb-4 text-sm">
							<li>出品情報が検索結果に表示されなくなる</li>
							<li>特集ストア・販促メールの掲載対象から外れる</li>
							<li>Amazonから画像変更の通知が届く</li>
						</ul>
						<p className="text-gray-700 mb-4">
							また、メイン画像にはロゴ・テキスト・透かし・枠線・カラーブロックも禁止されています。
							商品を画像の85%以上の領域に収めることも求められます。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							最適な画像設定の手順
						</h2>
						<ol className="list-decimal list-inside space-y-4 text-gray-700">
							<li>
								<strong>元の写真を高解像度で用意する</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									スマートフォンの最高画質モードやデジタルカメラで撮影。最終的に1600×1600px以上になるサイズで撮ります。
								</span>
							</li>
							<li>
								<strong>背景を純白（RGB 255,255,255）に変換する</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									AIツールで背景を除去し、白色に置き換えます。グレーがかった白ではなくRGB値が255,255,255の純白が必要です。
								</span>
							</li>
							<li>
								<strong>1000×1000px以上にリサイズする</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									推奨は1600×1600px。正方形（1:1）でなくても登録できますが、正方形が一番表示が整います。
								</span>
							</li>
							<li>
								<strong>JPEG形式で保存する</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									品質85〜90%程度に設定。ファイルサイズを抑えつつ画質を維持できます。
								</span>
							</li>
						</ol>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							しろぬきやで解決する方法
						</h2>
						<p className="text-gray-700 mb-4">
							背景を純白に変換し、1000×1000px以上にリサイズする作業を毎回手動で行うのは大変です。
							<strong>しろぬきや</strong>
							を使えば、AI背景除去＋Amazonの推奨サイズへの自動リサイズがブラウザ上で完結します。
							画像はサーバーに送信しないのでプライバシーも安心。無料で1日5枚まで利用できます。
						</p>
						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
